import { UIManager } from './ui-manager.js';
import { AudioManager } from './audio-manager.js';
import { SpeechManager } from './speech-manager.js';

export class SessionManager {
  private uiManager: UIManager;
  private audioManager: AudioManager;
  private speechManager: SpeechManager;

  private isRunning: boolean = false;
  private isProcessing: boolean = false;
  private isListening: boolean = false;
  private isPaused: boolean = false;
  private challengeStartTime: number | null = null;
  private consecutiveErrors: number = 0;
  private maxConsecutiveErrors: number = 2;

  constructor(uiManager: UIManager, audioManager: AudioManager, speechManager: SpeechManager) {
    this.uiManager = uiManager;
    this.audioManager = audioManager;
    this.speechManager = speechManager;
  }

  // Getters
  getIsRunning(): boolean { return this.isRunning; }
  getIsProcessing(): boolean { return this.isProcessing; }
  getIsListening(): boolean { return this.isListening; }
  getIsPaused(): boolean { return this.isPaused; }
  getChallengeStartTime(): number | null { return this.challengeStartTime; }
  getConsecutiveErrors(): number { return this.consecutiveErrors; }
  getMaxConsecutiveErrors(): number { return this.maxConsecutiveErrors; }

  // Setters
  setIsProcessing(value: boolean): void { this.isProcessing = value; }
  setIsListening(value: boolean): void { this.isListening = value; }
  setChallengeStartTime(time: number | null): void { this.challengeStartTime = time; }

  /**
   * Start the training session
   */
  start(): void {
    if (this.isRunning) return;

    this.uiManager.setStatus('initializing', 'Starting...');

    // Reset error counter when starting
    this.consecutiveErrors = 0;
    this.speechManager.resetConsecutiveErrors();

    this.isRunning = true;
    this.isProcessing = false;
    this.isListening = false;
    this.isPaused = false;
    this.uiManager.setButtonsState(true, false);
    this.uiManager.updateStartButtonText(false);
  }

  /**
   * Stop the training session
   */
  stop(): void {
    this.isRunning = false;
    this.isProcessing = false;
    this.isListening = false;
    this.isPaused = false;
    this.consecutiveErrors = 0; // Reset error counter
    this.uiManager.setButtonsState(false, true);
    this.uiManager.updateStartButtonText(false);

    this.audioManager.stopSpeaking();
    this.speechManager.stopListening();
    this.uiManager.setRecordingVisual(false);
    this.uiManager.setStatus('idle', 'Stopped');
    this.uiManager.setChallengeText('');
  }

  /**
   * Enter paused state when user doesn't respond for 30 seconds
   */
  enterPausedState(): void {
    console.log('Entering paused state - user needs to confirm they are still there');
    this.isPaused = true;
    this.isProcessing = false;
    this.isListening = false;
    this.consecutiveErrors = 0; // Reset error counter since this isn't really an error

    this.uiManager.setButtonsState(false, false); // Keep stop available
    this.uiManager.updateStartButtonText(true);

    this.uiManager.setRecordingVisual(false);
    this.uiManager.setStatus('paused', 'Are you still there? Press Continue or Space to resume.');
    this.uiManager.setChallengeText('Session paused - no response detected for 30 seconds');

    // Optional: speak the pause message
    this.audioManager.speak('Are you still there? Press continue to resume training.').catch(err => {
      console.log('Could not speak pause message:', err);
    });
  }

  /**
   * Resume from paused state
   */
  resumeFromPause(): void {
    console.log('Resuming from paused state');
    this.isPaused = false;
    this.isProcessing = false;
    this.isListening = false;

    this.uiManager.setButtonsState(true, false);
    this.uiManager.updateStartButtonText(false);
  }

  /**
   * Handle start/continue button (works for both initial start and resuming from pause)
   */
  handleStartContinue(): void {
    if (this.isPaused) {
      this.resumeFromPause();
    } else {
      this.start();
    }
  }

  /**
   * Increment consecutive errors
   */
  incrementConsecutiveErrors(): void {
    this.consecutiveErrors++;
  }

  /**
   * Reset consecutive errors
   */
  resetConsecutiveErrors(): void {
    this.consecutiveErrors = 0;
  }

  /**
   * Check if should stop due to too many errors
   */
  shouldStopDueToErrors(): boolean {
    return this.consecutiveErrors >= this.maxConsecutiveErrors;
  }
}